import React from 'react';
import { connect } from 'react-redux';
import { GridToolbar } from '@progress/kendo-react-grid';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { provideLocalizationService, registerForLocalization } from '@progress/kendo-react-intl';
import { Grid, InlineBadge } from '../../shared/components';
import { getGeneralCompetencyStatusThemeColor } from '../../../resources/generalCompetencyStatus';
import { loadMyClaimsListPage, findMyClaims, openDeleteClaimDialog, closeDeleteClaimDialog, confirmDeleteClaim } from '../actions/my-claims-list-page-actions';
import { withRouter } from '../../../components/withRouter';

class MyClaimsList extends React.PureComponent {
  constructor() {
    super();
    this.handlePageChange = this.handlePageChange.bind(this);
    this.handleAddClicked = this.handleAddClicked.bind(this);
    this.handleDeleteClick = this.handleDeleteClick.bind(this);
    this.handleDeleteDialogClose = this.handleDeleteDialogClose.bind(this);
    this.handleDeleteConfirm = this.handleDeleteConfirm.bind(this);
    this.handleRowClick = this.handleRowClick.bind(this);
    this.renderStatus = this.renderStatus.bind(this);
    this.renderActions = this.renderActions.bind(this);
  }

  async componentDidMount() {
    await this.props.loadMyClaimsListPage();
  }

  render() {
    const localizationService = provideLocalizationService(this);
    return (
      <>
        <Grid
          loading={this.props.loading}
          data={this.props.claims}
          skip={this.props.skip}
          take={this.props.take}
          total={this.props.total}
          onPageChange={this.handlePageChange}
          onRowClick={this.handleRowClick}
          rowLink={(dataItem) => `/my_general_competency_claims/${dataItem.id}/overview`}
          columns={[
            {
              field: 'title',
              className: 'align-left',
              headerClassName: 'align-left',
              title: localizationService.toLanguageString('myGeneralCompetencyClaims.title'),
              sortable: false
            },
            {
              field: 'organizer',
              className: 'align-left',
              headerClassName: 'align-left',
              title: localizationService.toLanguageString('myGeneralCompetencyClaims.organizer'),
              sortable: false,
              width: '200px'
            },
            {
              field: 'occurredAt',
              title: localizationService.toLanguageString('myGeneralCompetencyClaims.occurredAt'),
              format: '{0:yyyy-MM-dd}',
              sortable: false,
              width: '140px'
            },
            {
              field: 'minutes',
              title: localizationService.toLanguageString('myGeneralCompetencyClaims.minutes'),
              sortable: false,
              width: '120px'
            },
            {
              field: 'status',
              title: localizationService.toLanguageString('myGeneralCompetencyClaims.status'),
              sortable: false,
              width: '160px',
              cell: this.renderStatus
            },
            {
              field: 'attachmentFilename',
              title: localizationService.toLanguageString('myGeneralCompetencyClaims.attachment'),
              sortable: false,
              width: '160px'
            },
            {
              title: ' ',
              sortable: false,
              width: '60px',
              cell: this.renderActions
            }
          ]}
        >
          <GridToolbar>
            <Button themeColor='primary' onClick={this.handleAddClicked}>
              {localizationService.toLanguageString('myGeneralCompetencyClaims.addClaim')}
            </Button>
          </GridToolbar>
        </Grid>
        {this.props.isDeleteDialogVisible &&
          <Dialog title={localizationService.toLanguageString('custom.confirm')} onClose={this.handleDeleteDialogClose}>
            <p>{localizationService.toLanguageString('myGeneralCompetencyClaims.deleteClaimConfirm')}</p>
            <DialogActionsBar>
              <Button themeColor='primary' onClick={this.handleDeleteConfirm}>
                {localizationService.toLanguageString('buttons.delete')}
              </Button>
              <Button onClick={this.handleDeleteDialogClose}>
                {localizationService.toLanguageString('buttons.cancel')}
              </Button>
            </DialogActionsBar>
          </Dialog>
        }
      </>
    );
  }

  getLocalizedStatus(status, localizationService) {
    const statusMap = {
      'PendingReview': 'myGeneralCompetencyClaims.statusPendingReview',
      'Approved': 'myGeneralCompetencyClaims.statusApproved',
      'Rejected': 'myGeneralCompetencyClaims.statusRejected'
    };
    const key = statusMap[status];
    return key ? localizationService.toLanguageString(key) : status;
  }

  renderStatus(e) {
    const localizationService = provideLocalizationService(this);
    const { statusCode } = e.dataItem;
    return (
      <td className={e.className}>
        <InlineBadge themeColor={getGeneralCompetencyStatusThemeColor(statusCode)}>
          {this.getLocalizedStatus(statusCode, localizationService)}
        </InlineBadge>
      </td>
    );
  }

  renderActions(e) {
    const { id, statusCode } = e.dataItem;
    if (statusCode !== 'PendingReview') {
      return <td className={e.className || ''}></td>;
    }
    return (
      <td className={e.className || ''}>
        <button
          className='k-button k-button-sm k-button-flat k-button-flat-base'
          onClick={() => this.handleDeleteClick(id)}
        >
          <span className='k-icon k-i-delete'></span>
        </button>
      </td>
    );
  }

  handleRowClick(e) {
    const { id } = e.dataItem;
    this.props.navigate(`/my_general_competency_claims/${id}/overview`);
  }

  handleDeleteClick(id) {
    this.props.openDeleteClaimDialog(id);
  }

  handleDeleteDialogClose() {
    this.props.closeDeleteClaimDialog();
  }

  handleDeleteConfirm() {
    this.props.confirmDeleteClaim();
  }

  handleAddClicked() {
    this.props.navigate('/my_general_competency_claims/new');
  }

  async handlePageChange(e) {
    await this.props.findMyClaims({ skip: e.page.skip, take: e.page.take });
  }
}

const mapStateToProps = state => ({
  loading: state.myGeneralCompetencyClaimsListPage.loading,
  claims: state.myGeneralCompetencyClaimsListPage.claims,
  skip: state.myGeneralCompetencyClaimsListPage.skip,
  take: state.myGeneralCompetencyClaimsListPage.take,
  total: state.myGeneralCompetencyClaimsListPage.total,
  isDeleteDialogVisible: state.myGeneralCompetencyClaimsListPage.isDeleteDialogVisible
});

const mapDispatchToProps = dispatch => ({
  loadMyClaimsListPage: () => dispatch(loadMyClaimsListPage()),
  findMyClaims: (payload) => dispatch(findMyClaims(payload)),
  openDeleteClaimDialog: (id) => dispatch(openDeleteClaimDialog(id)),
  closeDeleteClaimDialog: () => dispatch(closeDeleteClaimDialog()),
  confirmDeleteClaim: () => dispatch(confirmDeleteClaim())
});

registerForLocalization(MyClaimsList);

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(MyClaimsList));
